// src/app/layout/component/app.layout.ts
import { CommonModule } from '@angular/common';
import { Component, Renderer2, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from '@angular/router';
import { combineLatest, filter, map, startWith } from 'rxjs';

import { AppSidebar } from './app.sidebar';
import { AppTopbar } from './app-topbar/app.topbar';
import { LoadingOverlayComponent } from './loading-overlay/loading-overlay';
import { SimulationPanelComponent } from '../../pages/simulation-panel/simulation-panel';

import { LayoutService } from '../service/layout.service';
import { ConditionStore } from '../../core/shared/condition.store';
import { LoadingService } from '../../core/shared/loading.service';
import { MeService } from '../../core/shared/me.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [CommonModule, AppTopbar, AppSidebar, RouterModule, SimulationPanelComponent, LoadingOverlayComponent],
  template: `
    <div class="layout-wrapper" [ngClass]="containerClass">
      <app-topbar></app-topbar>
      <app-sidebar></app-sidebar>
      <app-loading-overlay></app-loading-overlay>

      <div class="layout-main-container">
        <div class="layout-main">
          <div class="baseLayoutWrap">
            @if (!checkingAccess) {
              @if (!accessDenied) {
                <div
                  class="grid gap-4"
                  [ngClass]="
                    (showSimulation$ | async)
                      ? 'grid-cols-1 sm:[grid-template-columns:1fr_360px]'
                      : 'grid-cols-1'
                  "
                >
                  <div class="order-1">
                    @if (showTitle$ | async) {
                      <div class="layoutHeading">
                        @if ((finalTitle$ | async) === 'Hypertension Dashboard' || (finalTitle$ | async) === 'CVD Dashboard') {
                          <h2>Welcome to CARDIO4Cities</h2>
                          <p>Manage simulations and details with real-time insights</p>
                        } @else {
                          <h2>{{ finalTitle$ | async }}</h2>
                        }
                      </div>
                    }

                    <div class="leftPanelWrap">
                      <router-outlet></router-outlet>
                    </div>
                  </div>

                  <div class="order-1 w-full sm:w-[360px]" [class.hidden]="!(showSimulation$ | async)">
                    <app-simulation-panel></app-simulation-panel>
                  </div>
                </div>
              } @else {
                <div class="p-6">
                  <h2>Access denied</h2>
                  <p>
                    You currently don’t have access to this application. Please reach out to your administrator for assistance. In the meantime, you can try refreshing the page or logging out and back in to see if that resolves the issue.
                  </p>
                </div>
              }
            } @else {
            }
          </div>
        </div>
      </div>

      <div class="layout-mask animate-fadein"></div>
    </div>
  `
})
export class AppLayout {
  overlayMenuOpenSubscription: any;
  menuOutsideClickListener: any;

  @ViewChild(AppSidebar) appSidebar!: AppSidebar;
  @ViewChild(AppTopbar) appTopBar!: AppTopbar;

  title$!: ReturnType<AppLayout['buildTitle$']>;
  showTitle$!: ReturnType<AppLayout['buildShowTitle$']>;
  showSimulation$!: ReturnType<AppLayout['buildShowSimulation$']>;
  finalTitle$!: ReturnType<AppLayout['buildFinalTitle$']>;

  private conditionStore = inject(ConditionStore);
  condition$ = this.conditionStore.getCondition$();

  private meService = inject(MeService);
  private loadingService = inject(LoadingService);

  accessDenied = false;
  checkingAccess = true;

  constructor(
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router,
    private route: ActivatedRoute
  ) {
    this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
          if (this.isOutsideClicked(event)) this.hideMenu();
        });
      }
      if (this.layoutService.layoutState().staticMenuMobileActive) this.blockBodyScroll();
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => this.hideMenu());

    this.title$ = this.buildTitle$();
    this.showTitle$ = this.buildShowTitle$();
    this.showSimulation$ = this.buildShowSimulation$();
    this.finalTitle$ = this.buildFinalTitle$();

    if (environment.enableMeValidation) {
      this.seedLocalSessionForMe();
      this.checkAllowlistOnce();
    } else {
      this.accessDenied = false;
      this.layoutService.setAccessDenied(false);
      this.checkingAccess = false;
    }
  }

  private seedLocalSessionForMe(): void {
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (!isLocalhost) return;

    if (!sessionStorage.getItem('userEmail')) {
      sessionStorage.setItem('userEmail', 'pradeep.chidambaram@wipro.com');
    }

    if (!sessionStorage.getItem('session_id')) {
      const storedSessionId = localStorage.getItem('session_id');

      if (storedSessionId) {
        sessionStorage.setItem('session_id', storedSessionId);
      } else {
        const newSessionId = `sess_${Date.now()}`;
        sessionStorage.setItem('session_id', newSessionId);
        localStorage.setItem('session_id', newSessionId);
      }
    }

    sessionStorage.removeItem('meAllowed');
    sessionStorage.removeItem('meStatus');
    sessionStorage.removeItem('userEmailFromMe');
  }

  private async checkAllowlistOnce(): Promise<void> {
    try {
      this.loadingService.show?.('Checking access...');

      const storedAllowed = sessionStorage.getItem('meAllowed');
      if (storedAllowed === 'true') {
        this.accessDenied = false;
        this.layoutService.setAccessDenied(false);
        this.emitMeDataReady();
        return;
      }
      if (storedAllowed === 'false') {
        this.accessDenied = true;
        this.layoutService.setAccessDenied(true);
        this.emitMeDataReady();
        return;
      }

      let email = '';
      for (let attempt = 0; attempt < 3; attempt++) {
        email = (sessionStorage.getItem('userEmailFromMe') || sessionStorage.getItem('userEmail') || '')
          .trim()
          .toLowerCase();

        if (email) break;
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      const sessionId = this.ensureSessionId();

      if (!email) {
        console.warn('[AppLayout] Missing email; treating as access denied.');
        this.accessDenied = true;
        this.layoutService.setAccessDenied(true);
        try {
          sessionStorage.setItem('meAllowed', 'false');
        } catch { }
        this.emitMeDataReady();
        return;
      }

      const res = await this.meService.checkAllowlist({ email, sessionId, forceRefresh: true });

      if (res.status !== 'success' || res.allowed !== true) {
        this.accessDenied = true;
        this.layoutService.setAccessDenied(true);
        try {
          sessionStorage.setItem('meAllowed', 'false');
        } catch { }
      } else {
        this.accessDenied = false;
        this.layoutService.setAccessDenied(false);
        try {
          sessionStorage.setItem('meAllowed', 'true');
        } catch { }
      }

      this.emitMeDataReady();
    } catch (err) {
      console.error('[AppLayout] MeService.checkAllowlist() error:', err);
      this.accessDenied = true;
      this.layoutService.setAccessDenied(true);
      try {
        sessionStorage.setItem('meAllowed', 'false');
      } catch { }
      this.emitMeDataReady();
    } finally {
      this.checkingAccess = false;
      this.loadingService.hide?.();
    }
  }

  private emitMeDataReady(): void {
    try {
      window.dispatchEvent(new CustomEvent('me-data-ready'));
    } catch { }
  }

  private getDeepestActivatedRouteSnapshot(): ActivatedRouteSnapshot {
    let r: ActivatedRoute = this.route;
    while (r.firstChild) r = r.firstChild;
    return r.snapshot;
  }

  private buildTitle$() {
    return this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      startWith(null),
      map(() => this.getDeepestActivatedRouteSnapshot()),
      map((snap) => {
        const d = snap.data || {};
        return typeof d['title'] === 'string' ? d['title'] : '';
      })
    );
  }

  private buildShowTitle$() {
    return this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      startWith(null),
      map(() => this.getDeepestActivatedRouteSnapshot()),
      map((snap) => {
        const d = snap.data || {};
        return typeof d['showLabelInLayout'] === 'boolean' ? d['showLabelInLayout'] : true;
      })
    );
  }

  private buildShowSimulation$() {
    return this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      startWith(null),
      map(() => this.getDeepestActivatedRouteSnapshot()),
      map((snap) => {
        const d = snap.data || {};
        return typeof d['showSimulation'] === 'boolean' ? d['showSimulation'] : true;
      })
    );
  }

  private buildFinalTitle$() {
    const route$ = this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      startWith(null),
      map(() => this.getDeepestActivatedRouteSnapshot())
    );

    return combineLatest([route$, this.title$, this.condition$]).pipe(
      map(([snap, routeTitle, condition]) => {
        const path = snap.routeConfig?.path ?? '';
        const isDashboard =
          path === 'dashboard' || (snap.data && String(snap.data['title'] || '').toLowerCase().includes('dashboard'));

        if (isDashboard) {
          if (condition === 'CVD') return 'CVD Dashboard';
          return 'Hypertension Dashboard';
        }
        return routeTitle;
      })
    );
  }

  private ensureSessionId(): string {
    const key = 'session_id';

    let sessionId =
      (sessionStorage.getItem(key) || localStorage.getItem(key) || '').trim();

    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }

    try {
      sessionStorage.setItem(key, sessionId);
      localStorage.setItem(key, sessionId);
    } catch { }

    return sessionId;
  }

  isOutsideClicked(event: MouseEvent) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.layout-menu-button');
    const eventTarget = event.target as Node;

    return !(
      sidebarEl?.isSameNode(eventTarget) ||
      sidebarEl?.contains(eventTarget) ||
      topbarEl?.isSameNode(eventTarget) ||
      topbarEl?.contains(eventTarget)
    );
  }

  hideMenu() {
    this.layoutService.layoutState.update((prev) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
      activePath: ''
    }));

    this.layoutService.reset();

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }
    this.unblockBodyScroll();
  }

  blockBodyScroll(): void {
    document.body.classList.add('blocked-scroll');
  }

  unblockBodyScroll(): void {
    document.body.classList.remove('blocked-scroll');
  }

  get containerClass() {
    return {
      'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
      'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
      'layout-static-inactive':
        this.layoutService.layoutState().staticMenuDesktopInactive && this.layoutService.layoutConfig().menuMode === 'static',
      'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
      'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive
    };
  }

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) this.overlayMenuOpenSubscription.unsubscribe();
    if (this.menuOutsideClickListener) this.menuOutsideClickListener();
  }
}